// German (du) copy for /preview-first-uploads, translated from copy.en.ts. Same shape (checked by copy.test.ts).
// The second pass is always "die Originale" (never "volle Qualität"). The uploader's own labels are in English,
// so they're quoted as it shows them.
import type { PreviewFirstCopy } from "./copy.en";

const de: PreviewFirstCopy = {
  seo: {
    title: "Uploads mit Vorschau zuerst, in allen Plänen | Photohawk",
    description:
      "Neu im Web-Uploader von Photohawk, in allen Plänen: Von jedem Foto geht zuerst ein kleines Vorschaubild hoch. So kann deine Galerie innerhalb von Minuten online gehen, verkaufen und geteilt werden, während die Originale im Hintergrund nachkommen.",
  },
  hero: {
    badge: "Neu",
    eyebrow: "Im Web-Uploader · In allen Plänen",
    title: "Deine Galerie ist in Minuten online,",
    titleHighlight: "nicht erst nach Stunden",
    subtitle:
      "Der Web-Uploader schickt jetzt von jedem Foto zuerst ein kleines Vorschaubild. Deine Galerie kann mit diesen Vorschaubildern online gehen, sodass Käufer ihre Fotos finden, kaufen und teilen können, während die Originale im Hintergrund weiter hochgeladen werden.",
    ctaPrimary: "Uploader öffnen",
    ctaSecondary: "So funktioniert's",
    uploaderLink: "Alles Neue im Web-Uploader ansehen",
  },
  steps: {
    heading: "So funktioniert's",
    subtitle: "Vorschau zuerst gibt es im Web-Uploader in allen Plänen. Wenn du Fotos hinzufügst, zeigt er, wie lange der Upload auf welchem Weg dauert, und du wählst „Previews first, originals later“.",
    stepLabel: "Schritt {n}",
    items: [
      {
        id: "add",
        title: "Füge deine Fotos hinzu",
        body: "Zieh Fotos oder einen ganzen Ordner hinein, wie gewohnt. Dein Browser erstellt von jedem Foto ein kleines Vorschaubild, direkt auf deinem Computer.",
      },
      {
        id: "previews",
        title: "Vorschaubilder gehen zuerst hoch",
        body: "Die Vorschaubilder sind klein und deshalb schnell hochgeladen, selbst über eine langsame Verbindung vor Ort.",
      },
      {
        id: "live",
        title: "Deine Galerie geht online",
        body: "Veröffentliche sie, sobald die Vorschaubilder oben sind, oder setz das Häkchen bei „Publish the gallery as soon as the previews are up“, dann passiert das von selbst. Käufer können sofort stöbern, suchen, kaufen und teilen.",
      },
      {
        id: "full",
        title: "Die Originale kommen nach",
        body: "Die Originale werden im Hintergrund weiter hochgeladen, in dieselben Fotos. Downloads und Abzüge nutzen immer die Originale.",
      },
    ],
    timeline: {
      label: "Ein Beispiel-Upload, von Anfang bis Ende",
      previews: "Vorschaubilder",
      previewsTime: "Minuten",
      live: "Galerie online",
      full: "Originale",
      fullTime: "im Hintergrund",
    },
  },
  buyers: {
    heading: "Was Käufer sehen",
    subtitle: "Nichts Neues zu lernen. Käufer bekommen die Galerie, die sie kennen, nur früher.",
    items: [
      {
        id: "protected",
        title: "Mit Wasserzeichen und geschützt, wie immer",
        body: "Käufer sehen dieselben Vorschaubilder mit Wasserzeichen wie immer, und der Vorschauschutz funktioniert genauso, wenn deine Galerie ihn nutzt.",
      },
      {
        id: "search",
        title: "Sie finden sich sofort",
        body: "Selfie- und Startnummernsuche funktionieren mit den Vorschaubildern, sodass alle ihre Fotos finden, sobald die Galerie online ist.",
      },
      {
        id: "delivery",
        title: "Originale, automatisch ausgeliefert",
        body: "Ein Kauf oder ein kostenloser Download wird nie als Vorschaubild ausgeliefert. Kauft oder lädt jemand ein Foto herunter, bevor sein Original da ist, rückt das Original in der Warteschlange nach vorn, und die Person bekommt es, sobald es bereit ist.",
      },
    ],
  },
  strip: {
    heading: "Behalte alles im Blick, während du hochlädst",
    body: "Während Vorschaubilder und Originale hochgeladen werden, zeigt ein Streifen oben im Uploader, wo deine Galerie steht: ob sie schon online ist, wie viele Originale noch fehlen und wann ein gekauftes Foto nach vorn gerückt ist. Sobald die Galerie online ist, zeigt er auch, wie viele Personen sie in den letzten 30 Minuten besucht haben, und Inhaber und Administratoren des Arbeitsbereichs sehen neue Bestellungen seit Beginn des Uploads.",
    exampleLabel: "Beispiel",
    live: "Online mit Vorschaubildern",
    orders: "3 Bestellungen seit dem Start",
    visitors: "42 Besucher in den letzten 30 Min.",
    left: "Noch 1.250 Originale",
    jumped: "Originale von 2 bestellten Fotos nach vorn gerückt",
  },
  faq: {
    heading: "Fragen",
    items: [
      {
        q: "Muss ich den Uploader geöffnet lassen?",
        a: "Am besten ja, bis die Originale fertig sind; der Streifen zeigt, wie viele noch fehlen. Schließt du ihn früher, geht nichts verloren. Deine Galerie bleibt mit den Vorschaubildern online, und wenn du diese Galerie das nächste Mal im Uploader öffnest, zeigt er dir, wie viele Fotos noch warten. Füge denselben Ordner noch einmal hinzu (auch später, von zu Hause), und nur die fehlenden Originale werden hochgeladen.",
      },
      {
        q: "Was, wenn jemand ein Foto kauft, bevor sein Original da ist?",
        a: "Kaufen geht trotzdem. Solange der Uploader läuft, rückt das Original dieses Fotos in der Warteschlange nach vorn. Die Bestell-E-Mail wartet auf die Datei und geht wenige Minuten nach ihrem Eintreffen raus, sodass der Käufer nie versehentlich das Vorschaubild bekommt. War der Uploader geschlossen, wartet die Bestellung, bis du den Ordner erneut hinzufügst. Der Uploader zeigt dir, auf welche Fotos ein Käufer wartet, und diese gehen zuerst hoch.",
      },
      {
        q: "Funktioniert das auch bei Galerien mit kostenlosen Downloads?",
        a: "Ja. Alle können ansehen, suchen und herunterladen, sobald die Vorschaubilder oben sind, und ein Download ist nie das Vorschaubild. Lädt jemand ein Foto herunter, bevor sein Original da ist, rückt das Original in der Warteschlange nach vorn: Der Download zeigt an, dass das Original noch hochgeladen wird, und funktioniert, sobald die Datei ankommt. Ein per E-Mail verschickter Download geht dann ebenfalls raus. Jeder Download zählt nur einmal.",
      },
      {
        q: "Funktionieren Selfie- und Startnummernsuche mit den Vorschaubildern?",
        a: "Ja. Gesichter und Startnummern werden auf den Vorschaubildern erkannt, die Suche funktioniert also, sobald sie oben sind. Solange der Uploader geöffnet ist, schickt er außerdem schärfere Nahaufnahmen aus deinen Originalen, die der Suche helfen.",
      },
      {
        q: "Welche Pläne enthalten das?",
        a: "In allen Plänen, auch in Lite. Hat dein Arbeitsbereich Vorschau zuerst schon genutzt, werden diese Fotos ganz normal fertig: Füge denselben Ordner erneut hinzu, dann werden ihre Originale hochgeladen, und ihre Bestellungen und Downloads kommen weiterhin an.",
      },
      {
        q: "Kostet das extra?",
        a: "Nein. Es ist in allen Plänen enthalten, und jedes Foto zählt nur einmal: Das Vorschaubild und sein Original sind dasselbe Foto. Der einzige Unterschied ist, dass deine Verbindung insgesamt etwas mehr Daten überträgt (etwa 5 % mehr).",
      },
      {
        q: "Funktioniert das mit FTP und Photohawk Studio?",
        a: "Im Moment nicht. Vorschau zuerst ist Teil des Web-Uploaders. Per FTP oder aus Photohawk Studio gesendete Fotos werden wie bisher in einem Durchgang als Originale hochgeladen.",
      },
      {
        q: "Ist es standardmäßig an?",
        a: "Nein. Jedes Mal, wenn du Fotos hinzufügst, zeigt der Web-Uploader, wie lange sie mit und ohne Vorschau zuerst brauchen, und ausgewählt ist, die Originale direkt hochzuladen. Wähle „Previews first, originals later“, wenn die Galerie früher online sein soll; der Web-Uploader merkt sich deine Wahl fürs nächste Mal. Mit einer kleineren Upload-Auflösung („Large“ oder „Web“) werden nur verkleinerte Dateien hochgeladen, dann gilt Vorschau zuerst nicht.",
      },
    ],
  },
  cta: {
    heading: "Probier es beim nächsten Upload aus",
    body: "Vorschau zuerst gibt es im Web-Uploader in allen Plänen. Füge deine Fotos hinzu, wähle Vorschau zuerst, und deine Galerie kann in Minuten online sein.",
    button: "Uploader öffnen",
  },
};

export default de;
